import { Dispatch, SetStateAction, useState } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button } from "@mui/material";
import * as Yup from "yup";
import { isValidInput } from "../utils/validations";
import useTranslation from "../hooks/traslation";
import Loader from "../constants/Loader";

interface AddBankModalProps {
  open: boolean;
  onClose: () => void;
  name: string;
  selectedId: string;
  setName: Dispatch<SetStateAction<string>>;
  setSelectedId: Dispatch<SetStateAction<string>>;
  handleSubmit: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isFormLoading?: boolean;
}

export default function AddBank({
  open,
  setOpen,
  name,
  setName,
  handleSubmit,
  selectedId,
  setSelectedId,
  onClose,
  isFormLoading = false,
}: AddBankModalProps) {
  const [errors, setErrors] = useState({ name: "" });
  const translate = useTranslation();

  const bankSchema = Yup.object().shape({
    name: Yup.string()
      .required(translate?.banks?.errorName || "Bank name is required")
      .min(2, translate?.banks?.errorCatLimitMin || "Minimum 2 characters")
      .max(50, translate?.banks?.errorCatLimitMax || "Maximum 50 characters"),
  });

  const validateForm = async () => {
    try {
      await bankSchema.validate({ name }, { abortEarly: false });
      setErrors({ name: "" });
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = { name: "" };
        err.inner.forEach((error) => {
          if (error.path) {
            newErrors[error.path as keyof typeof newErrors] = error.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleFormSubmit = async () => {
    if (await validateForm()) {
      handleSubmit();
    }
  };

  const handleClose = () => {
    if (isFormLoading) return;
    setSelectedId("");
    setOpen(false);
    setErrors({ name: "" });
  };

  return (
    <Modal
      className="modal addCategory_modal"
      open={open}
      onClose={isFormLoading ? undefined : handleClose}
    >
      <div className="modal-dialog w_900">
        <div className="modal-body">
          <Loader isLoad={isFormLoading} />
          <div className="modal_title">
            <h2>{selectedId ? "Update Bank" : "Add Bank"}</h2>
            <div className="btn-close" onClick={handleClose}>
              <CloseIcon />
            </div>
          </div>
          <form className="form" action="">
            <div className="gap_p">
              <div className="control_group w_50">
                <label>Bank Name</label>
                <TextField
                  placeholder="Enter bank name"
                  fullWidth
                  value={name}
                  onChange={(e) => {
                    if (isValidInput(e.target.value)) {
                      setName(e.target.value);
                      setErrors((prev) => ({ ...prev, name: "" }));
                    }
                  }}
                  error={!!errors.name}
                  hiddenLabel
                  disabled={isFormLoading}
                />
                {errors.name && (
                  <span
                    className="error-message"
                    style={{
                      color: "red",
                      fontSize: "12px",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    {errors.name}
                  </span>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button
                  className="btn btn_grey"
                  onClick={handleClose}
                  disabled={isFormLoading}
                >
                  {translate?.main?.cancel || "Cancel"}
                </Button>
                <Button
                  className="btn btn_primary"
                  onClick={handleFormSubmit}
                  disabled={isFormLoading}
                >
                  {selectedId
                    ? translate?.main?.update || "Update"
                    : translate?.globals?.submit || "Submit"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
